/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import useTitle from 'utils/customHook/useTitle'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material'
import NumberField from 'components/forms/NumberField'
import Input from 'components/forms/Input'
import SimpleTextArea from '../../../../components/forms/SimpleTextArea'
import ProgressCard from '../../../../components/card/ProgressCard'
import PopulationField from '../components/PopulationField'
import { formatThousands } from '../../../utils/RPQSUtils'
import Select from 'components/forms/Select'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import ReferencialAction from 'referencial/action/ReferencialAction'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import Checkbox from 'components/forms/Checkbox'
import { CardTitle } from 'components/card/NewCard'
import { CardTable } from 'components/datatable/NewTable'
import ContactAction from 'referencial/components/contact/actions/ContactAction'
import { mainBlue } from 'utils/constants/ColorTheme'
import useActions from 'utils/customHook/useActions'
import RpqsAction from 'rpqs/actions/RpqsAction'
import { push } from '@lagunovsky/redux-react-router'
import { PATH_RPQS_REFERENCIAL_COLLECTIVITY, PATH_RPQS_REFERENCIAL_COLLECTIVITY_NEW } from 'home/constants/RouteConstants'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import DashboardIcon from '@mui/icons-material/Dashboard'
import LocationPinIcon from '@mui/icons-material/LocationPin'
import HandshakeIcon from '@mui/icons-material/Handshake'
import ContactsIcon from '@mui/icons-material/Contacts'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import DialogContacts from '../components/DialogContacts'
import CityAction from 'referencial/components/city/actions/CityAction'
import DialogCities from '../components/DialogCities'
import SectorAction from 'referencial/components/sector/actions/SectorAction'
import DialogManagementUnits from '../components/DialogManagementUnits'

const HEADERS_POPULATION = ['city', 'year', 'totalPopulationINSEE', 'temporaryResidents', 'adjustedTotalPopulation']
const HEADERS_MANAGEMENT_ENTITIES = ['name', 'production', 'protection', 'job', 'transport', 'storage', 'distribution']
const HEADERS_CONTACTS = ['name', 'phoneTel', 'email', 'socialReason']

const CollectivityDashboardApp = () => {
    const { id } = useParams()

    const {
        accountUser,
        collectivityProps,
        contacts,
        cities,
        citiesPopulations,
        sectors,
        managementUnits,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        collectivityProps: store.RpqsReducer.collectivity,
        contacts: store.ContactReducer.contacts,
        cities: store.CityReducer.cities,
        citiesPopulations: store.RpqsReducer.citiesPopulations,
        sectors: store.SectorReducer.sectors,
        managementUnits: store.RpqsReducer.managementUnits,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(ReferencialAction.fetchSandreCodes())
        dispatch(ContactAction.fetchContacts())
        dispatch(CityAction.fetchCities())
        dispatch(SectorAction.fetchSectors())
        dispatch(RpqsAction.fetchAllManagementUnits())
    }, [dispatch, id])

    const [isEditMode, setIsEditMode] = useState(false)
    const [collectivity, setCollectivity] = useState({})

    const [openDialogContacts, setOpenDialogContacts] = useState(false)
    const [selectedContacts, setSelectedContacts] = useState([])

    const [openDialogCities, setOpenDialogCities] = useState(false)
    const [selectedCities, setSelectedCities] = useState([])

    const [openDialogManagementUnits, setOpenDialogManagementUnits] = useState(false)
    const [selectedManagementUnits, setSelectedManagementUnits] = useState([])

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

    const isLoaded = id === 'new' || collectivityProps?.id === parseInt(id)

    useEffect(() => {
        if (id === 'new') {
            setCollectivity({})
            setIsEditMode(true)
        } else if (isLoaded) {
            setCollectivity(collectivityProps)
        }
    }, [collectivityProps, id, isLoaded])

    useTitle(() => {
        if (id !== 'new') {
            return [{
                title: i18n.rpqs,
                href: 'rpqs',
            }, {
                title: i18n.RPQSReferencials,
                href: 'rpqs/referencials',
            }, {
                title: i18n.collectivities,
                href: PATH_RPQS_REFERENCIAL_COLLECTIVITY,
            }, {
                title: collectivity.name,
                href: `${PATH_RPQS_REFERENCIAL_COLLECTIVITY}/${collectivity.id}`,
            }]
        }
        return [{
            title: i18n.rpqs,
            href: 'rpqs',
        }, {
            title: i18n.RPQSReferencials,
            href: 'rpqs/referencials',
        }, {
            title: i18n.collectivities,
            href: PATH_RPQS_REFERENCIAL_COLLECTIVITY,
        }, {
            title: i18n.new,
            href: PATH_RPQS_REFERENCIAL_COLLECTIVITY_NEW,
        }]
    }, [collectivity, id])

    const disabled = useMemo(() => ({
        active: isEditMode,
        disabled: !isEditMode,
    }), [isEditMode])

    useEffect(() => {
        setSelectedContacts(collectivity?.link_contacts?.map((contact) => contact.contactCode) ?? [])
    }, [collectivity.link_contacts])

    const dataContacts = useMemo(() => {
        const contactsFiltered = contacts.filter((contact) => selectedContacts?.find(s => s === contact.id))
        return contactsFiltered.map((contact) => ({
            name: { value: contact.name },
            phoneTel: { value: contact.phoneTel },
            email: { value: contact.email },
            socialReason: { value: contact.socialReason },
        }))
    }, [selectedContacts, contacts])

    useEffect(() => {
        setSelectedCities(collectivity?.link_cities?.map((city) => city.cityCode) ?? [])
    }, [collectivity.link_cities])

    useEffect(() => {
        if (selectedCities.length) dispatch(CityAction.getCitiesPopulations(selectedCities))
    }, [dispatch, selectedCities])

    const citiesWithPopulation = useMemo(() => {
        const citiesFiltered = cities.filter((city) => selectedCities?.find(s => s === city.id))
        return citiesFiltered.map(city => {
            const population = citiesPopulations.find(p => p.cityCode === city.code)
            return {
                ...city,
                year: population?.year,
                inseeTotal: population?.inseeTotal,
                temporaryResident: population?.temporaryResident,
                increasedTotal: population?.increasedTotal,
            }
        })
    }, [selectedCities, citiesPopulations, cities])

    const dataCities = useMemo(() => citiesWithPopulation?.map((cP) => ({
        city: { value: cP.name },
        year: { value: cP.year },
        totalPopulationINSEE: { value: formatThousands(cP.inseeTotal) },
        temporaryResidents: { value: formatThousands(cP.temporaryResident) },
        adjustedTotalPopulation: { value: formatThousands(cP.increasedTotal) },
    })), [citiesWithPopulation])

    const inseeTotal = useMemo(() =>
        citiesWithPopulation.reduce((acc, city) =>
            acc + (city.inseeTotal ?? 0)
        , 0)
    , [citiesWithPopulation])

    const temporaryResident = useMemo(() =>
        citiesWithPopulation.reduce((acc, city) =>
            acc + (city.temporaryResident ?? 0)
        , 0)
    , [citiesWithPopulation])

    const increasedTotal = useMemo(() =>
        citiesWithPopulation.reduce((acc, city) =>
            acc + (city.increasedTotal ?? 0)
        , 0)
    , [citiesWithPopulation])

    useEffect(() => {
        setSelectedManagementUnits(collectivity?.link_managementUnits?.map((mU) => mU.managementUnitCode) ?? [])
    }, [collectivity.link_managementUnits])

    const skillsList = useSandreList(SANDRE.ENTITE_GESTION_COMPETENCE)
    const collectivityTypes = useSandreList(SANDRE.COLLECTIVITE_TYPES)
    const contactFunctions = useSandreList(SANDRE.CONTACT_FONCTION)

    const dataManagementUnits = useMemo(() => {
        const managementUnitsFiltered = managementUnits.filter(mU => selectedManagementUnits?.find(s => s === mU.id))
        return managementUnitsFiltered.map(mU => ({
            id: mU.id,
            name: { value: mU.name },
            production: { value: skillsList.find(s => s.code === mU.productionSkill)?.name },
            protection: { value: skillsList.find(s => s.code === mU.protectionSkill)?.name },
            job: { value: skillsList.find(s => s.code === mU.treatmentSkill)?.name },
            transport: { value: skillsList.find(s => s.code === mU.transportSkill)?.name },
            storage: { value: skillsList.find(s => s.code === mU.storageSkill)?.name },
            distribution: { value: skillsList.find(s => s.code === mU.distributionSkill)?.name },
        }))
    }, [selectedManagementUnits, managementUnits, skillsList])

    const isConsultant = useMemo(() => accountUser.consultant === '1', [accountUser])

    const actionsContacts = [{
        icon: 'edit',
        color: 'white',
        displayed: isEditMode,
        onClick: () => {
            setOpenDialogContacts(true)
        },
        tooltip: i18n.addContributor,
    }]

    const actionsCities = [{
        icon: 'edit',
        color: 'white',
        displayed: isEditMode,
        onClick: () => {
            setOpenDialogCities(true)
        },
        tooltip: i18n.addCity,
    }]

    const actionsManagementUnits = [{
        icon: 'edit',
        color: 'white',
        displayed: isEditMode,
        onClick: () => {
            setOpenDialogManagementUnits(true)
        },
        tooltip: i18n.addManagementEntity,
    }]


    useActions(() => {
        if (isConsultant) {
            return {}
        }
        const isNewActions = id === 'new'
        const formattedCollectivity = {
            ...collectivity,
            id: -1,
        }
        const formattedLink = (collectiviteCode) => ({
            link_contacts: selectedContacts.map(contactId => ({
                collectiviteCode,
                contactCode: contactId,
            })),
            link_cities: selectedCities.map(cityCode => ({
                collectiviteCode,
                cityCode,
            })),
            link_managementUnits: selectedManagementUnits.map(managementUnitCode => ({
                collectiviteCode,
                managementUnitCode,
            })),
        })
        const actions = (() => {
            if (isNewActions) {
                return {
                    save: () => {
                        dispatch(RpqsAction.createCollectivity(formattedCollectivity, formattedLink))
                        setIsEditMode(false)
                    },
                    cancel: () => {
                        dispatch(push(PATH_RPQS_REFERENCIAL_COLLECTIVITY))
                        setIsEditMode(false)
                    },
                }
            }
            if (isEditMode) {
                return {
                    save: () => {
                        dispatch(RpqsAction.updateCollectivity({
                            ...formattedCollectivity,
                            ...formattedLink(parseInt(id)),
                            id: parseInt(id),
                        }, id))
                        setIsEditMode(false)
                    },
                    cancel: () => {
                        setCollectivity(collectivityProps)

                        setSelectedContacts(collectivityProps?.link_contacts?.map((contact) => contact.contactCode) ?? [])
                        setSelectedCities(collectivity?.link_cities?.map((city) => city.cityCode) ?? [])
                        setSelectedManagementUnits(collectivity?.link_managementUnits?.map((mU) => mU.managementUnitCode) ?? [])

                        setIsEditMode(false)
                    },
                }
            }
            return {
                edit: () => {
                    setIsEditMode(true)
                },
                deleteCheck: () => {
                    setOpenDeleteDialog(true)
                },
            }
        })()
        return actions
    }, [isConsultant, collectivity, id, isEditMode, selectedContacts, selectedCities, selectedManagementUnits])

    if (!isLoaded) {
        return <ProgressCard indeterminate round />
    }

    return (
        <>
            <Grid container spacing={1}>
                <Grid container size={6} direction='column' spacing={1}>
                    <Card>
                        <CardTitle title={ i18n.description} icon={<DashboardIcon fontSize='large'/>} />
                        <CardContent>
                            <Grid container spacing={1}>
                                <Grid size={3}>
                                    <NumberField
                                        title={ i18n.code }
                                        value={ collectivity.code }
                                        maxLength={10}
                                        onChange={c => setCollectivity({ ...collectivity, code: c })}
                                        {...disabled}
                                    />
                                </Grid>
                                <Grid size={9}>
                                    <Input
                                        title={ i18n.name }
                                        value={ collectivity.name }
                                        onChange={name => setCollectivity({ ...collectivity, name })}
                                        {...disabled}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        label={i18n.collectivityType}
                                        options={collectivityTypes}
                                        value={ collectivity.collectiviteType }
                                        onChange={collectiviteType => setCollectivity({ ...collectivity, collectiviteType })}
                                        {...disabled}
                                    />
                                </Grid>
                                <Grid size={2}>
                                    <SimpleDatePicker
                                        label={ i18n.creationDate }
                                        value={ collectivity.creationDate }
                                        onChange={creationDate => setCollectivity({ ...collectivity, creationDate })}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={2}>
                                    <SimpleDatePicker
                                        label={ i18n.endDate }
                                        value={ collectivity.endDate }
                                        onChange={endDate => setCollectivity({ ...collectivity, endDate })}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Input
                                        title={ i18n.sispeaCode }
                                        value={ collectivity.sispeaCode }
                                        onChange={sispeaCode => setCollectivity({ ...collectivity, sispeaCode })}
                                        maxLength={14}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Input
                                        title={ i18n.siren }
                                        value={ collectivity.siren }
                                        onChange={siren => setCollectivity({ ...collectivity, siren })}
                                        maxLength={14}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Input
                                        title={ i18n.insee }
                                        value={ collectivity.inseeNumber }
                                        onChange={inseeNumber => setCollectivity({ ...collectivity, inseeNumber })}
                                        maxLength={14}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        label={i18n.sector}
                                        options={sectors}
                                        value={collectivity.sectorCode}
                                        onChange={sectorCode => setCollectivity({ ...collectivity, sectorCode })}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid container size={8} alignItems='center' paddingTop={1}>
                                    <Checkbox
                                        label={ i18n.collectivityWithSingleEntity }
                                        checked={ collectivity.isSingleManagementUnit }
                                        onChange={isSingleManagementUnit => setCollectivity({ ...collectivity, isSingleManagementUnit })}
                                        { ...disabled }
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardTitle title={i18n.address} icon={<LocationPinIcon fontSize='large'/>}/>
                        <CardContent>
                            <Grid container spacing={1}>
                                <Grid size={6}>
                                    <Select
                                        label={i18n.function}
                                        options={contactFunctions}
                                        value={ collectivity.addressFunction }
                                        onChange={addressFunction => setCollectivity({ ...collectivity, addressFunction })}
                                        {...disabled}
                                    />
                                </Grid>
                                <Grid offset={6}/>
                                <Grid size={6}>
                                    <Input
                                        title={ i18n.address }
                                        value={ collectivity.address }
                                        onChange={ address => setCollectivity({ ...collectivity, address }) }
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Input
                                        title={ i18n.addressComplement }
                                        value={ collectivity.addressComplement }
                                        onChange={ addressComplement => setCollectivity({ ...collectivity, addressComplement }) }
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Input
                                        title={ i18n.postalCode }
                                        value={ collectivity.postalCode }
                                        onChange={ postalCode => setCollectivity({ ...collectivity, postalCode }) }
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={9}>
                                    <Input
                                        title={ i18n.city }
                                        value={ collectivity.city }
                                        onChange={ city => setCollectivity({ ...collectivity, city }) }
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Input
                                        title={ i18n.sigle }
                                        value={ collectivity.acronym }
                                        onChange={ acronym => setCollectivity({ ...collectivity, acronym }) }
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={9}>
                                    <Input
                                        title={ i18n.headquarters }
                                        value={ collectivity.headquarters }
                                        onChange={ headquarters => setCollectivity({ ...collectivity, headquarters }) }
                                        { ...disabled }
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid container size={6} direction='column' spacing={1}>
                    <Card>
                        <CardTitle title={i18n.skillsApplied} icon={<MenuBookIcon fontSize='large'/>}/>
                        <CardContent>
                            <Grid container spacing={1}>
                                <Grid container size={4} alignItems='center' paddingTop={1}>
                                    <Checkbox
                                        label={ i18n.usageDrink }
                                        checked={ collectivity.hasAEP_skill }
                                        onChange={hasAEP_skill => setCollectivity({ ...collectivity, hasAEP_skill })}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Input
                                        title={ i18n.code }
                                        value={ collectivity.AEP_code }
                                        maxLength={14}
                                        onChange={AEP_code => setCollectivity({ ...collectivity, AEP_code })}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid offset={1} />
                                <Grid container size={4} alignItems='center' paddingTop={1}>
                                    <Checkbox
                                        label={ i18n.publicSanitation }
                                        checked={ collectivity.hasSanitationSkill }
                                        onChange={hasSanitationSkill => setCollectivity({ ...collectivity, hasSanitationSkill })}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Input
                                        title={ i18n.code }
                                        value={ collectivity.sanitationCode }
                                        maxLength={14}
                                        onChange={sanitationCode => setCollectivity({ ...collectivity, sanitationCode })}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid offset={2} />
                            </Grid>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardTitle title={i18n.contacts} icon={<ContactsIcon fontSize='large'/>}/>
                        <CardContent>
                            <Grid container spacing={1}>
                                <Grid size={4}>
                                    <Input
                                        title={ i18n.title }
                                        value={ collectivity.contactTitle }
                                        onChange={ contactTitle => setCollectivity({ ...collectivity, contactTitle }) }
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Input
                                        title={ i18n.phoneTel }
                                        value={ collectivity.contactPhone }
                                        onChange={ contactPhone => setCollectivity({ ...collectivity, contactPhone }) }
                                        maxLength={14}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Input
                                        title={ i18n.email }
                                        value={ collectivity.email }
                                        onChange={ email => setCollectivity({ ...collectivity, email }) }
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <SimpleTextArea
                                        title={ i18n.openHours }
                                        value={ collectivity.openHours }
                                        onChange={ openHours => setCollectivity(prev => ({ ...prev, openHours })) }
                                        rows={4}
                                        maxLength={255}
                                        disabled={ !isEditMode }
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <CardTable
                                        title={i18n.contacts}
                                        headers={HEADERS_CONTACTS}
                                        rows={dataContacts}
                                        color={mainBlue}
                                        actions={actionsContacts}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid container size={12} direction='column' spacing={1}>
                    <Card>
                        <CardTitle title={i18n.members} icon={<HandshakeIcon fontSize='large'/>}/>
                        <CardContent>
                            <Grid container spacing={1}>
                                <Grid size={6}>
                                    <PopulationField
                                        title={ i18n.totalPopulationINSEE }
                                        value={ collectivity.inseeTotalAuto ? inseeTotal : collectivity.inseeTotal }
                                        onChange={insTotal => setCollectivity({ ...collectivity, inseeTotal: insTotal })}
                                        disabled={ !isEditMode || collectivity.inseeTotalAuto}
                                    />
                                </Grid>
                                <Grid container size={3} alignItems='center' paddingTop={1}>
                                    <Checkbox
                                        label={ i18n.calculAuto }
                                        checked={ collectivity.inseeTotalAuto }
                                        onChange={inseeTotalAuto => setCollectivity({ ...collectivity, inseeTotalAuto })}
                                        {...disabled}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={1}>
                                <Grid size={6}>
                                    <PopulationField
                                        title={ i18n.temporaryResidents }
                                        value={ collectivity.temporaryResidentAuto ? temporaryResident : collectivity.temporaryResident }
                                        onChange={tempResident => setCollectivity({ ...collectivity, temporaryResident: tempResident })}
                                        disabled={ !isEditMode || collectivity.temporaryResidentAuto}
                                    />
                                </Grid>
                                <Grid container size={3} alignItems='center' paddingTop={1}>
                                    <Checkbox
                                        label={ i18n.calculAuto }
                                        checked={ collectivity.temporaryResidentAuto }
                                        onChange={temporaryResidentAuto => setCollectivity({ ...collectivity, temporaryResidentAuto })}
                                        { ...disabled }
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={1}>
                                <Grid size={6}>
                                    <PopulationField
                                        title={ i18n.adjustedTotalPopulation }
                                        value={ collectivity.increasedTotalAuto ? increasedTotal : collectivity.increasedTotal }
                                        onChange={incrTotal => setCollectivity({ ...collectivity, increasedTotal: incrTotal })}
                                        disabled={ !isEditMode || collectivity.increasedTotalAuto}
                                    />
                                </Grid>
                                <Grid container size={3} alignItems='center' paddingTop={1}>
                                    <Checkbox
                                        label={ i18n.calculAuto }
                                        checked={ collectivity.increasedTotalAuto }
                                        onChange={increasedTotalAuto => setCollectivity({ ...collectivity, increasedTotalAuto })}
                                        { ...disabled }
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <CardTable
                                        title={i18n.city}
                                        rows={dataCities}
                                        headers={HEADERS_POPULATION}
                                        color={mainBlue}
                                        actions={actionsCities}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    <CardTable
                        title={i18n.managementEntities}
                        headers={HEADERS_MANAGEMENT_ENTITIES}
                        rows={dataManagementUnits}
                        actions={actionsManagementUnits}
                        onClickRow={me => {
                            if (!isEditMode) dispatch(push(`/rpqs/referencials/managementEntities/${me.id}/dashboard`))
                        }}
                        color={mainBlue}
                    />
                </Grid>
            </Grid>
            <DialogContacts
                open={openDialogContacts}
                onClose={() => {
                    setSelectedContacts(collectivity?.link_contacts?.map((contact) => contact.contactCode) ?? [])
                    setOpenDialogContacts(false)
                }}
                onValidate={() => setOpenDialogContacts(false)}

                contacts={contacts}
                selectedContacts={selectedContacts}
                setSelectedContacts={setSelectedContacts}
            />
            <DialogCities
                open={openDialogCities}
                onClose={() => {
                    setSelectedCities(collectivity?.link_cities?.map((city) => city.cityCode) ?? [])
                    setOpenDialogCities(false)
                }}
                onValidate={() => setOpenDialogCities(false)}

                cities={cities}
                selectedCities={selectedCities}
                setSelectedCities={setSelectedCities}
            />
            <DialogManagementUnits
                open={openDialogManagementUnits}
                onClose={() => {
                    setSelectedManagementUnits(collectivity?.link_managementUnits?.map((mU) => mU.managementUnitCode) ?? [])
                    setOpenDialogManagementUnits(false)
                }}
                onValidate={() => setOpenDialogManagementUnits(false)}

                managementUnits={managementUnits}
                selectedManagementUnits={selectedManagementUnits}
                setSelectedManagementUnits={setSelectedManagementUnits}
            />
            <Dialog open={openDeleteDialog}>
                <DefaultDialogTitle title={i18n.collectivity} onClose={() => setOpenDeleteDialog(false)} />
                <DialogContent>
                    <Typography>
                        {i18n.sureDeleteCollectivity}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>
                        {i18n.no}
                    </Button>
                    <Button
                        onClick={() => {
                            setOpenDeleteDialog(false)
                            dispatch(RpqsAction.deleteCollectivity(parseInt(id)))
                        }}
                        variant='contained'
                        color='primary'
                    >
                        {i18n.yes}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default CollectivityDashboardApp